import {PropertyDefinitionForm} from '@github-ui/custom-properties-core/components/PropertyDefinitionForm'
import {useBannerContext} from '@github-ui/custom-properties-core/context/BannerContext'
import {createDefinitionForm} from '@github-ui/custom-properties-core/definition-form'
import type {DefinitionFormSchema, DefinitionValidator} from '@github-ui/custom-properties-core/definition-form-types'
import {validatePropertyNameAsync} from '@github-ui/custom-properties-core/helpers/validate'
import {
  getFirstInvalidFieldKey,
  usePropertyDefinitionForm,
} from '@github-ui/custom-properties-core/hooks/use-property-definition-form'
import {useRefs} from '@github-ui/custom-properties-core/hooks/use-refs'
import type {
  PropertiesLimits,
  PropertyDefinition,
  PropertyNameOrgConflicts,
  ValuesEditableBy,
} from '@github-ui/custom-properties-types'
import type {SafeHTMLString} from '@github-ui/safe-html'
import sudo from '@github-ui/sudo'
import {useAnalytics} from '@github-ui/use-analytics'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {SparkleFillIcon} from '@primer/octicons-react'
import {Checkbox, FormControl, Label} from '@primer/react'
import {useEffect, useEffectEvent, useMemo, useRef, useState} from 'react'

import {useAllowedValuesSuggester} from '../hooks/use-allowed-values-suggester'
import {useSavePropertyPath, useSuggestAllowedValuesPath} from '../hooks/use-properties-paths'
import {usePropertySource} from '../hooks/use-property-source'
import {useSuggestedDefinition} from '../hooks/use-suggested-definition'
import styles from './DefinitionForm.module.css'

interface RepoPropertyDefinitionSchema extends DefinitionFormSchema {
  requireExplicitValues: boolean
  valuesEditableBy: ValuesEditableBy
}

interface Props {
  definition?: PropertyDefinition
  propertiesLimits: PropertiesLimits
  existingPropertyNames: string[]
  setFormError: (error: SafeHTMLString) => void
  onCancel: () => void
  onSuccess: (propertyName: string) => void
}

export function DefinitionForm({
  definition,
  propertiesLimits,
  existingPropertyNames,
  setFormError,
  onCancel,
  onSuccess,
}: Props) {
  const {settingsLevel, sourceName} = usePropertySource()
  const {setBanner, bannerRef, banner} = useBannerContext()
  const {sendAnalyticsEvent} = useAnalytics()
  const [orgConflicts, setOrgConflicts] = useState<PropertyNameOrgConflicts>()
  const {getRef, setRef} = useRefs<RepoPropertyDefinitionSchema>()
  const allowedValuesSuggestionAcceptedRef = useRef(false)
  const [saving, setSaving] = useState(false)
  const savePropertyPath = useSavePropertyPath()

  const isEditing = Boolean(definition)

  const businessSlug = settingsLevel === 'business' ? sourceName : undefined
  const form = useMemo(
    () =>
      createDefinitionForm<RepoPropertyDefinitionSchema>([
        {
          field: 'propertyName',
          validate: validatePropertyName(businessSlug, setOrgConflicts),
        },
      ]),
    [businessSlug],
  )

  useEffect(() => {
    bannerRef?.current?.focus()
  }, [banner, bannerRef])

  const suggestedDefinition = useSuggestedDefinition()

  const shouldReportInvalidScenario = isEditing && suggestedDefinition
  useEffect(() => {
    if (shouldReportInvalidScenario) {
      const timeout = setTimeout(() => {
        throw new Error('Cannot have a suggested definition when editing an existing definition.')
      })
      return () => clearTimeout(timeout)
    }
  }, [shouldReportInvalidScenario])

  const initialValues = definition || suggestedDefinition

  const {updateField, getFieldValue, getFieldError, validateForm, validateField} =
    usePropertyDefinitionForm<RepoPropertyDefinitionSchema>({
      form,
      formMode: isEditing ? 'update' : 'create',
      propertiesLimits,
      existingPropertyNames,
      initialValues: {
        propertyName: initialValues?.propertyName || '',
        description: initialValues?.description || '',
        valueType: initialValues?.valueType || 'string',
        allowedValues: initialValues?.allowedValues || [],
        defaultValue: initialValues?.defaultValue || '',
        regexEnabled: !!initialValues?.regex,
        regexPattern: initialValues?.regex || '',
        required: initialValues?.required || false,
        newAllowedValue: '',
        valuesEditableBy: initialValues?.valuesEditableBy || 'org_actors',
        requireExplicitValues: initialValues?.requireExplicitValues || false,
      },
    })

  async function saveDefinition() {
    /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- Safe empty string initialization. SECURITY XSS risk: Do not modify with user input. Verified: only replaced with server-sanitized HTML. */
    setFormError('' as SafeHTMLString)
    const errors = await validateForm()

    if (errors.formErrors.length) {
      /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- TODO: [SECURITY] Potential XSS risk. Pre-existing infraction - DO NOT COPY. Must be fixed. */
      return setFormError(errors.formErrors[0] as SafeHTMLString)
    }

    const key = getFirstInvalidFieldKey(errors)
    if (key) {
      return getRef(key)?.focus()
    }

    setSaving(true)

    if (!(await sudo())) {
      /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- Static string literal is safe */
      setFormError('Unauthorized' as SafeHTMLString)
      setSaving(false)
      return
    }

    const valueType = getFieldValue('valueType')
    const allowedValues =
      valueType === 'single_select' || valueType === 'multi_select' ? getFieldValue('allowedValues') : null
    const regex = (getFieldValue('regexEnabled') && getFieldValue('regexPattern')) || null

    const body = {
      propertyName: getFieldValue('propertyName'),
      valueType,
      required: getFieldValue('required'),
      defaultValue: getFieldValue('required') ? getFieldValue('defaultValue') : null,
      description: getFieldValue('description').trim() || null,
      allowedValues,
      valuesEditableBy: getFieldValue('valuesEditableBy'),
      regex,
      requireExplicitValues: getFieldValue('required') && getFieldValue('requireExplicitValues'),
    }

    try {
      const result = await verifiedFetchJSON(savePropertyPath, {
        method: 'POST',
        body,
      })

      if (result.ok) {
        setBanner(isEditing ? 'definition.updated.success' : 'definition.created.success')

        if (!isEditing && suggestedDefinition) {
          sendAnalyticsEvent('repo_custom_properties_suggestions.icebreakers.property_created', undefined, {
            propertyName: suggestedDefinition.propertyName,
            keptSuggestedName: body.propertyName === suggestedDefinition.propertyName,
          })
        }

        if (allowedValuesSuggestionAcceptedRef.current) {
          sendAnalyticsEvent('repo_custom_properties_suggestions.allowed_values.suggestion_accepted_and_property_saved')
        }

        onSuccess(body.propertyName)
      } else {
        const responseBody = await result.json()
        /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- TODO: [SECURITY] Potential XSS risk. Pre-existing infraction - DO NOT COPY. Must be fixed. */
        setFormError((responseBody?.error || 'Something went wrong.') as SafeHTMLString)
        setSaving(false)
      }
    } catch {
      /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- Static string literal is safe */
      setFormError('Something went wrong.' as SafeHTMLString)
      setSaving(false)
    }
  }

  const valueType = getFieldValue('valueType')
  const isSelect = valueType === 'single_select' || valueType === 'multi_select'
  const isText = valueType === 'string'

  const setNewAllowedValueRef = useMemo(() => setRef('newAllowedValue'), [setRef])

  const [allowedValuesSuggestionDismissed, setAllowedValuesSuggestionDismissed] = useState(false)
  const {suggestedAllowedValues, suggestAllowedValues} = useAllowedValuesSuggester({
    endpointPath: useSuggestAllowedValuesPath(),
    valueType,
    onSuggestionSuccess: () => setAllowedValuesSuggestionDismissed(false),
  })

  const canShowSuggestedAllowedValues = suggestedAllowedValues.length > 0 && !allowedValuesSuggestionDismissed

  const suggestionParams = {
    propertyName: getFieldValue('propertyName'),
    description: getFieldValue('description'),
    existingAllowedValues: getFieldValue('allowedValues'),
  }

  // Suggest allowed values if form is opened in edit mode with a select-type property.
  const suggestAllowedValuesOnMount = useEffectEvent(() => {
    if (isEditing && isSelect) {
      suggestAllowedValues(suggestionParams)
    }
  })
  useEffect(() => {
    suggestAllowedValuesOnMount()
  }, [])

  function onAcceptSuggestion() {
    updateField('allowedValues', [...new Set(getFieldValue('allowedValues').concat(suggestedAllowedValues))])
    setAllowedValuesSuggestionDismissed(true)
    allowedValuesSuggestionAcceptedRef.current = true
  }

  return (
    <>
      <PropertyDefinitionForm>
        <PropertyDefinitionForm.NameField
          inputRef={setRef('propertyName')}
          readonly={isEditing}
          value={getFieldValue('propertyName')}
          validationError={getFieldError('propertyName')}
          onChange={value => updateField('propertyName', value)}
          onBlur={() => suggestAllowedValues(suggestionParams)}
          orgConflicts={orgConflicts}
        />

        <PropertyDefinitionForm.DescriptionField
          inputRef={setRef('description')}
          value={getFieldValue('description')}
          validationError={getFieldError('description')}
          onChange={value => updateField('description', value)}
          onBlur={() => suggestAllowedValues(suggestionParams)}
        />

        <div className="d-flex flex-row flex-items-center gap-3">
          <PropertyDefinitionForm.TypeSelector
            value={valueType}
            readonly={isEditing}
            onChange={value => updateField('valueType', value)}
          />
          {isSelect && canShowSuggestedAllowedValues && (
            <Label variant="secondary" className="mt-2">
              <SparkleFillIcon size={12} className="mr-1 color-fg-accent" />
              <span className="color-fg-accent">
                {suggestedAllowedValues.length} suggestion{suggestedAllowedValues.length !== 1 ? 's' : ''}
              </span>
            </Label>
          )}
        </div>

        {isSelect && (
          <PropertyDefinitionForm.AllowedValuesEditor
            setNewAllowedValueRef={setNewAllowedValueRef}
            allowedValues={getFieldValue('allowedValues')}
            newAllowedValue={getFieldValue('newAllowedValue')}
            newAllowedValueError={getFieldError('newAllowedValue')}
            defaultValue={getFieldValue('defaultValue')}
            onNewAllowedValueChange={value => updateField('newAllowedValue', value)}
            onAllowedValuesChange={async ({type, values}) => {
              if (type === 'add') {
                if (await validateField('newAllowedValue')) {
                  return getRef('newAllowedValue')?.focus()
                }
                updateField('newAllowedValue', '', {skipValidation: true})
              }

              updateField('allowedValues', values)

              suggestAllowedValues({...suggestionParams, existingAllowedValues: values})
            }}
            suggestionProps={
              canShowSuggestedAllowedValues
                ? {
                    suggestedValues: suggestedAllowedValues,
                    onAccept: onAcceptSuggestion,
                    onDismiss: () => setAllowedValuesSuggestionDismissed(true),
                  }
                : undefined
            }
          />
        )}

        <PropertyDefinitionForm.AdditionalSettingsSection>
          {isText && (
            <PropertyDefinitionForm.RegexEditor
              inputRef={setRef('regexPattern')}
              isEnabled={getFieldValue('regexEnabled')}
              pattern={getFieldValue('regexPattern') || ''}
              validationError={getFieldError('regexPattern')}
              onEnabledChange={enabled => updateField('regexEnabled', enabled)}
              onPatternChange={pattern => updateField('regexPattern', pattern, {debounce: true})}
            />
          )}
          <FormControl>
            <Checkbox
              checked={getFieldValue('valuesEditableBy') === 'org_and_repo_actors'}
              onChange={({target}) => {
                updateField('valuesEditableBy', target.checked ? 'org_and_repo_actors' : 'org_actors')
                if (!target.checked) {
                  updateField('requireExplicitValues', false)
                }
              }}
            />
            <FormControl.Label>Allow repository actors to set this property</FormControl.Label>
            <FormControl.Caption>
              Repository users and apps with the repository-level &quot;custom properties&quot; fine-grained permission
              can set and update the value for their repository.
            </FormControl.Caption>
          </FormControl>
          <PropertyDefinitionForm.RequiredDefinitionEditor
            checkboxLabel="Require this property for all repositories"
            checkboxCaption="Repositories that don't have an explicit value for this property will inherit the default value."
            defaultValueCaption="This is the default value that will be set for this property. Changing it has immediate effect, although on large organizations it may take minutes to be available in search results."
            setDefaultValueInputRef={setRef('defaultValue')}
            isRequired={getFieldValue('required')}
            valueType={valueType}
            defaultValue={getFieldValue('defaultValue') || ''}
            defaultValueError={getFieldError('defaultValue')}
            allowableDefaultValues={getFieldValue('allowedValues')}
            propertyName={getFieldValue('propertyName')}
            onRequiredChange={required => {
              updateField('required', required)
              if (!required) {
                updateField('requireExplicitValues', false)
              }
            }}
            onDefaultValueChange={value => updateField('defaultValue', value, {debounce: true})}
            requiredFormExtension={
              <FormControl className={styles.explicitValueFormControl}>
                <FormControl.Label>Require explicit user-specified values</FormControl.Label>
                <FormControl.Caption>
                  When creating, transferring, or updating the property values of repositories, users must provide an
                  explicit value.
                </FormControl.Caption>
                <Checkbox
                  checked={getFieldValue('requireExplicitValues')}
                  onChange={() => updateField('requireExplicitValues', !getFieldValue('requireExplicitValues'))}
                />
              </FormControl>
            }
          />
        </PropertyDefinitionForm.AdditionalSettingsSection>

        <PropertyDefinitionForm.Actions isSaving={saving} onSave={saveDefinition} onCancel={onCancel} />
      </PropertyDefinitionForm>
    </>
  )
}

function validatePropertyName(
  businessSlug: string | undefined,
  onOrgConflicts: (conflicts: PropertyNameOrgConflicts | undefined) => void,
): DefinitionValidator<RepoPropertyDefinitionSchema> {
  return async (data, errors, ctx) => {
    if (!businessSlug) return
    if (ctx.formMode === 'update') return

    onOrgConflicts(undefined)

    // Save server request if property name is invalid for other reasons
    if (errors.fieldErrors.propertyName?.length) {
      return
    }

    const {propertyName} = data

    const error = await validatePropertyNameAsync(businessSlug, propertyName)

    if (error) {
      if (error?.orgConflicts) {
        onOrgConflicts(error.orgConflicts)
      }
      return {fieldErrors: {propertyName: [error.message]}}
    }
  }
}
